/**
 * @fileoverview Learning Test Data (Shared Question Definitions)
 * @module @monorepo/types
 *
 * Static question definitions for the learning type test.
 * Used by both frontend (test display) and backend (AI analysis).
 */

import learningTestData from './learningTest.json';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Learning test section definition
 */
export interface LearningTestSection {
	id: string;
	title: string;
	description: string | null;
	explanation: string;
	questionRange: [number, number];
	phase: 1 | 2;
}

/**
 * Learning test question definition
 */
export interface LearningTestQuestion {
	id: number;
	text: string;
	section: string;
}

/**
 * Complete learning test data structure
 */
export interface LearningTestData {
	metadata: {
		version: string;
		totalQuestions: number;
		questionRange: { min: number; max: number };
		lastUpdated: string;
	};
	wizardPhases: Array<{
		id: number;
		title: string;
		description: string;
		sections: string[];
		totalQuestions: number;
		note?: string;
	}>;
	sections: LearningTestSection[];
	questions: LearningTestQuestion[];
}

// ============================================================================
// EXPORTS
// ============================================================================

/**
 * The complete learning test data including sections and questions
 */
export const LEARNING_TEST_DATA: LearningTestData = learningTestData as LearningTestData;

/**
 * All sections for the learning test
 */
export const LEARNING_TEST_SECTIONS: LearningTestSection[] = LEARNING_TEST_DATA.sections;

/**
 * All questions for the learning test
 */
export const LEARNING_TEST_QUESTIONS: LearningTestQuestion[] = LEARNING_TEST_DATA.questions;

/**
 * Get a section by ID
 */
export function getLearningTestSection(sectionId: string): LearningTestSection | undefined {
	return LEARNING_TEST_SECTIONS.find((s) => s.id === sectionId);
}

/**
 * Get questions for a section
 */
export function getLearningTestQuestionsBySection(sectionId: string): LearningTestQuestion[] {
	return LEARNING_TEST_QUESTIONS.filter((q) => q.section === sectionId);
}
